import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppStore } from '../store/useAppStore';
import { useRequireAllAnswered } from '../hooks/useRouteGuards';
import {
  CONTEXT_GROUPS,
  CONTEXT_GROUP_ORDER,
  CONTEXT_GROUP_PATHS,
} from '../features/session/contextGroups';
import { QUESTION_IDS } from '../types/ids';
import { Button } from '../components/ui/Button';
import { trackEvent } from '../utils/posthogClient';

export function ReviewPage() {
  const ready = useRequireAllAnswered();
  const navigate = useNavigate();
  const session = useAppStore((state) => state.session);
  const dataModel = useAppStore((state) => state.dataModel);
  const computeAndStoreResult = useAppStore((state) => state.computeAndStoreResult);
  const stepEnabled = useAppStore((state) => state.settings.featureFlags.reviewStep.enabled);
  const analyticsEnabled = useAppStore((state) => state.settings.featureFlags.analytics.enabled);

  useEffect(() => {
    if (!ready || stepEnabled) return;
    void navigate('/ergebnis', { replace: true });
  }, [ready, stepEnabled, navigate]);

  if (!ready || !session || !stepEnabled) return null;

  function handleShowResult(): void {
    computeAndStoreResult();
    void navigate('/ergebnis');
  }

  return (
    <div className="page-container stack">
      <h1>Überprüfe deine Antworten</h1>

      <div className="cluster-grid">
        {CONTEXT_GROUP_ORDER.map((group) => (
          <section key={group} className="card stack">
            <h2>
              {group === 'angebot' && 'Angebot'}
              {group === 'instagram' && 'Instagram'}
              {group === 'arbeitsweise' && 'Arbeitsweise'}
            </h2>
            <ul className="stack">
              {CONTEXT_GROUPS[group].map((fieldId) => {
                const field = dataModel.contextQuestions.find((q) => q.id === fieldId);
                const value = session.context[fieldId];
                const label = field?.options.find((option) => option.value === value)?.label;
                return (
                  <li key={fieldId} className="review-item">
                    <span>
                      <strong>{field?.title}:</strong> {label ?? '—'}
                    </span>
                    <Link
                      to={CONTEXT_GROUP_PATHS[group]}
                      state={{ from: 'review' }}
                      className="link-action"
                      onClick={() => {
                        if (analyticsEnabled) {
                          trackEvent('review_edit_click', { target: `context_${group}` });
                        }
                      }}
                    >
                      Ändern
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>

      <section className="card stack">
        <h2>Deine Testantworten</h2>
        <ul className="stack">
          {QUESTION_IDS.map((questionId, index) => {
            const question = dataModel.questions.find((q) => q.id === questionId);
            const answer = session.answers[questionId];
            const label = question?.options.find((option) => option.id === answer?.optionId)?.label;
            return (
              <li key={questionId} className="review-item">
                <span>
                  <strong>{question?.title}:</strong> {label}
                </span>
                <Link
                  to={`/test/${index + 1}`}
                  state={{ from: 'review' }}
                  className="link-action"
                  onClick={() => {
                    if (analyticsEnabled) {
                      trackEvent('review_edit_click', { target: `question_${index + 1}` });
                    }
                  }}
                >
                  Ändern
                </Link>
              </li>
            );
          })}
        </ul>
      </section>

      <section className="card card--muted">
        <p>
          Das Ergebnis bewertet die strukturelle Eignung von Instagram. Es bewertet weder deinen
          persönlichen Wert noch automatisch die Qualität deines Produktes.
        </p>
      </section>

      <Button onClick={handleShowResult}>Ergebnis anzeigen</Button>
    </div>
  );
}
